// Rollover page — multi-week chain progression.
//
// Rewired to the Football Products design kit (FpHead, FpScoreboard,
// FpSkeleton, FpAwait, FpDisclaimer, FpSeam, FpCard). All chrome comes
// from the `.fp` scoped layer; product layout remains intact.

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FpHead,
  FpScoreboard,
  FpSkeleton,
  FpAwait,
  FpDisclaimer,
  FpSeam,
  FpCard,
  fmtNum,
  DASH,
} from "../football/_fpkit.tsx";
import {
  DriftDecisionModal,
  RolloverIntelligenceView,
  RolloverTierPerformance,
  RolloverTrackerSummary,
  SkipCycleBanner,
} from "../../components/rollover-extras";
import { useUserTier } from "../../auth/useUserTier.ts";
import { useAuthSession } from "../../auth/useAuthSession.ts";
import { useTrial } from "../../conversion/useTrial.ts";
import type { ConversionCtaState } from "../../components/rollover-extras";
import { useRolloverTracker } from "../../rollover/useRolloverTracker.ts";
import { usePhantomCycle } from "../../phantom/usePhantomCycle.ts";
import { usePhantomCycleHistory } from "../../phantom/usePhantomCycleHistory.ts";
import { usePitchPicks } from "../../pitch/usePitchPicks.ts";
import { useStakeRecommendation } from "../../rollover/useStakeRecommendation.ts";
import { useSikaGoals } from "../../sika/useSikaGoals.ts";
import { useCompoundingDecision } from "../../vault/useCompoundingDecision.ts";
import { useRolloverChain } from "../../vault/useRolloverChain.ts";
import { useRolloverProtection } from "../../vault/useRolloverProtection.ts";
import { DoctrineSection } from "../../components/DoctrineSection.tsx";
import { useDoctrinePicks } from "../../doctrine/useDoctrinePicks.ts";
import { RolloverChainRail } from "./RolloverChainRail.tsx";
import { RolloverChainRules } from "./RolloverChainRules.tsx";
import { RolloverCycleHistory } from "./RolloverCycleHistory.tsx";
import { RolloverStakeDecider } from "./RolloverStakeDecider.tsx";
import { RolloverWeekBrief } from "./RolloverWeekBrief.tsx";

function shortDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

// Derive upcoming-bet combined odds from a cycle's selections jsonb.
function combinedOddsFromSelections(selections: unknown): number | null {
  if (!Array.isArray(selections) || selections.length === 0) return null;
  let product = 1;
  let count = 0;
  for (const leg of selections as Array<Record<string, unknown>>) {
    const raw = leg?.odds ?? leg?.decimal_odds ?? leg?.price;
    const v = typeof raw === "number" ? raw : typeof raw === "string" ? Number(raw) : NaN;
    if (Number.isFinite(v) && v > 1) {
      product *= v;
      count += 1;
    }
  }
  return count > 0 ? +product.toFixed(3) : null;
}

function fmtCcy(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return DASH;
  return Math.round(n).toLocaleString();
}

const todayStr = () =>
  new Date().toLocaleDateString(undefined, {
    weekday: "long",
    month: "short",
    day: "numeric",
  });

export function RolloverPage(): JSX.Element {
  const tracker = useRolloverTracker();
  const { isPro } = useUserTier();
  const { user } = useAuthSession();
  const trialState = useTrial();
  const ctaState: ConversionCtaState = useMemo(() => {
    if (isPro) return "pro";
    if (!user) return "anon";
    const status = trialState.trial?.status as string | undefined;
    if (status === "active") return "trial_active";
    if (status) return "trial_used";
    return "trial_eligible";
  }, [isPro, user, trialState.trial]);
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);
  const chainState = useRolloverChain();
  const cycleState = usePhantomCycle();
  const historyState = usePhantomCycleHistory();
  const decisionState = useCompoundingDecision(cycleState.cycle?.id ?? null);
  const cycleIds = useMemo(
    () => historyState.cycles.map((c) => c.id),
    [historyState.cycles],
  );
  const protectionState = useRolloverProtection(chainState.chain?.id ?? null, cycleIds);
  const goalsState = useSikaGoals();
  const pitchState = usePitchPicks();

  const upcomingOdds = useMemo<number | null>(() => {
    const fromCycle = combinedOddsFromSelections(cycleState.cycle?.selections);
    if (fromCycle && fromCycle > 1) return fromCycle;
    const target = chainState.chain?.odds_target;
    return typeof target === "number" && target > 1 ? target : null;
  }, [cycleState.cycle?.selections, chainState.chain?.odds_target]);
  const recommendationState = useStakeRecommendation(
    chainState.chain?.id ?? null,
    upcomingOdds,
    null,
    cycleState.cycle?.confidence_score ?? null,
  );

  const chain = chainState.chain;
  const cycle = cycleState.cycle;
  const history = historyState.cycles;
  const goal = useMemo(() => {
    if (!chain) return goalsState.goals[0] ?? null;
    if (chain.sika_goal_id) {
      return goalsState.goals.find((g) => g.id === chain.sika_goal_id) ?? null;
    }
    return goalsState.goals[0] ?? null;
  }, [chain, goalsState.goals]);

  const chainShortId = chain ? chain.id.slice(-6).toUpperCase() : null;
  const totalWeeks = chain?.total_rollovers ?? 0;
  const currentWeek = chain?.current_week ?? 0;
  const startedAt = shortDate(chain?.created_at);

  const loading = chainState.loading || historyState.loading || cycleState.loading;
  const fatalError = chainState.error;

  const {
    probabilityPicks: doctrineProbPicks,
    valuePicks: doctrineValuePicks,
    substrateEmpty,
    substrateWeek,
  } = useDoctrinePicks({ t1HasPicks: !!chain });

  // ── Loading ─────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="fp" data-screen-label="03 ROLLOVER">
        <FpHead kicker="ROLLOVER" title={`Loading · ${todayStr()}`} />
        <FpSkeleton rows={3} />
      </div>
    );
  }

  // ── Empty — no chain ────────────────────────────────────────
  if (!chain) {
    return (
      <div className="fp" data-screen-label="03 ROLLOVER">
        <FpHead kicker="ROLLOVER" title={todayStr()} />
        <FpAwait
          kicker="ROLLOVER"
          title="No active chain yet."
          body="A rollover chain appears here once the Phantom cycle pipeline produces one for your account. STRIKZA never invents a chain in progress and never publishes fictional historical streaks."
        />
        <DoctrineSection
          probabilityPicks={doctrineProbPicks}
          valuePicks={doctrineValuePicks}
          isFullFallback
          substrateEmpty={substrateEmpty}
          substrateWeek={substrateWeek}
        />
      </div>
    );
  }

  // ── Scoreboard data ─────────────────────────────────────────
  const weeksDone = Math.max(0, currentWeek - 1);
  const wins = history.filter((c) => c.status === "phantom_won").length;
  const streakTotal = Math.max(weeksDone, wins);
  const goalTarget = goal?.target_amount ?? null;
  const goalCurrent = goal?.current_amount ?? null;
  const goalPct =
    goalTarget && goalTarget > 0 && goalCurrent !== null
      ? Math.min(100, Math.round((goalCurrent / goalTarget) * 100))
      : null;
  const currentValue = chain.current_value ?? null;
  const opening = chain.opening_stake ?? null;

  const headTitle = chainShortId
    ? `#${chainShortId} · ${todayStr()}`
    : todayStr();

  return (
    <div className="fp" data-screen-label="03 ROLLOVER">
      <FpHead
        kicker="ROLLOVER"
        title={headTitle}
        badge={startedAt ? `Since ${startedAt}` : undefined}
        status={cycle ? `Week ${currentWeek || 1}` : undefined}
        live={!!cycle}
      />

      <DriftDecisionModal />

      <FpDisclaimer text="Safe Mode active. Auto-protect on each win · Chain auto-pauses on first loss. STRIKZA never places bets — you keep manual control." />

      {fatalError !== null ? (
        <>
          <FpAwait
            kicker="ROLLOVER"
            title="Chain unavailable."
            body={fatalError}
            icon="info"
          />
          <DoctrineSection
            probabilityPicks={doctrineProbPicks}
            valuePicks={doctrineValuePicks}
            isFullFallback
            substrateEmpty={substrateEmpty}
            substrateWeek={substrateWeek}
          />
        </>
      ) : (
        <>
          <FpScoreboard
            tiles={[
              {
                label: `Active stake · Wk ${currentWeek || 1}`,
                value: `GH₵${fmtCcy(currentValue)}`,
                sub: opening
                  ? `Seed GH₵${fmtCcy(opening)} · ${wins} ${wins === 1 ? "win" : "wins"}`
                  : undefined,
                live: true,
              },
              {
                label: "Chain progress",
                value: `${weeksDone} / ${totalWeeks || DASH}`,
                sub: `Week ${currentWeek || 1} of ${totalWeeks || DASH} · ${chain.cadence ?? "daily"}`,
              },
              {
                label: "Win streak",
                value: `${wins} / ${streakTotal || DASH}`,
                sub: wins > 0
                  ? `${wins} resolved ${wins === 1 ? "win" : "wins"}`
                  : "No resolved wins yet",
              },
              {
                label: "Goal funded",
                value: goalPct !== null ? `${goalPct}%` : DASH,
                sub: goal && goalTarget !== null && goalCurrent !== null
                  ? `GH₵${fmtCcy(goalCurrent)} / GH₵${fmtCcy(goalTarget)}`
                  : "No Sika goal linked",
              },
            ]}
          />

          <RolloverChainRail
            chain={chain}
            activeCycle={cycle}
            history={history}
            cumulativeProtected={protectionState.cumulativeProtected}
          />

          <SkipCycleBanner />

          <FpSeam />

          <FpCard
            kicker="SELECTION TRACKER"
            action={
              <Link
                to="/rollover/chains"
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 10,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "var(--fp-accent-text)",
                  textDecoration: "none",
                }}
              >
                View all chains →
              </Link>
            }
          >
            <RolloverTrackerSummary
              cycles={tracker.cycles}
              expandedRunId={expandedRunId}
              loading={tracker.loading}
              error={tracker.error}
              onToggle={(rid) =>
                setExpandedRunId((cur) => (cur === rid ? null : rid))
              }
              renderExpanded={(c) => (
                <RolloverIntelligenceView
                  cycle={c}
                  isPro={isPro}
                  ctaState={ctaState}
                />
              )}
            />
            <RolloverTierPerformance
              winRates={tracker.winRates}
              cycles={tracker.cycles}
            />
          </FpCard>

          {/* This week's tier-grouped picks */}
          <RolloverWeekBrief />

          <FpSeam />

          <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 12, alignItems: "start" }}>
            <RolloverStakeDecider
              chain={chain}
              cycle={cycle}
              decision={decisionState}
              recommendation={recommendationState}
            />
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <RolloverChainRules setOn={startedAt} />
              <RolloverCycleHistory
                cycles={history}
                chainCreatedAt={chain?.created_at ?? null}
                chainShortId={chainShortId}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
